import { UpdateDocumentDigitalVersionModel } from 'types/dynamic-forms/UpdateDocumentDigitalVersionModel';
import { ResolvedDigitalVersion } from 'types/dynamic-forms/ResolvedDigitalVersion';
import { dynamicFormsAxiosInstance as axiosInstance } from './apiCommon';
import { DocumentModel } from 'types/dynamic-forms/DocumentModel';
import { DocumentDashboardModel } from 'types/dynamic-forms/DocumentDashboardModel';
import { CreateDocumentModel } from 'types/dynamic-forms/CreateDocumentModel';
import { DocumentVersionModel } from 'types/dynamic-forms/DocumentVersionModel';
import { DocumentDiff } from 'types/dynamic-forms/DocumentDiff';
import { DocumentCollectionModel } from 'types/dynamic-forms/DocumentCollectionModel';
import { EPDCommentLogModel } from 'types/epd-definition/EPDCommentLogModel';

export const getDocumentCommentLog = async (docId: string): Promise<EPDCommentLogModel> =>
  (await axiosInstance.get<EPDCommentLogModel>(`/api/v1/dynamic-forms/documents/${docId}/comment-log`)).data;

// The digital version (and with it the schema) the document would move to for this PCR and c-PCR.
export const resolveDigitalVersion = async (
  docId: string,
  pcrId: string | null,
  cpcrId: string | null
): Promise<ResolvedDigitalVersion> =>
  (
    await axiosInstance.get<ResolvedDigitalVersion>(`/api/v1/dynamic-forms/documents/${docId}/digital-version/resolve`, {
      params: { pcrId: pcrId ?? undefined, cpcrId: cpcrId ?? undefined },
    })
  ).data;

export const getPropertiesSchema = async (schemaId: string) =>
  (await axiosInstance.get(`/api/v1/dynamic-forms/schemas/${schemaId}/properties`)).data;

export const getPortalSchema = async (schemaId: string) =>
  (await axiosInstance.get(`/api/v1/dynamic-forms/schemas/${schemaId}/portal`)).data;

export const getVersionLogSchema = async (schemaId: string) =>
  (await axiosInstance.get(`/api/v1/dynamic-forms/schemas/${schemaId}/version-log`)).data;

export const getVersionComparisonSchema = async (schemaId: string) =>
  (await axiosInstance.get(`/api/v1/dynamic-forms/schemas/${schemaId}/version-comparison`)).data;

export const getDeclarationPdfSchema = async (schemaId: string) =>
  (await axiosInstance.get(`/api/v1/dynamic-forms/schemas/${schemaId}/declaration-pdf`)).data;

/**
 * Pass versionId when showing a specific (e.g. older) version, so options that depend on the stored data
 * (a discontinued verifier) match that version; without it the current version is used.
 */
export const getDocumentDataSources = async (schemaId: string, docId: string, versionId?: string) =>
  (await axiosInstance.get(`/api/v1/dynamic-forms/schemas/${schemaId}/data-sources`, { params: { docId, versionId } }))
    .data;

// The data sources of the document's current version; the server resolves its schema, so this does not wait for the document.
export const getCurrentDocumentDataSources = async (docId: string) =>
  (await axiosInstance.get(`/api/v1/dynamic-forms/documents/${docId}/data-sources`)).data;

export const getDocumentDataSource = async (schemaId: string, dataSourceId: string, docId: string) =>
  (await axiosInstance.get(`/api/v1/dynamic-forms/schemas/${schemaId}/data-sources/${dataSourceId}`, { params: { docId } }))
    .data;

export const postCreateDocument = async (model: CreateDocumentModel) =>
  (await axiosInstance.post<string>(`/api/v1/dynamic-forms/documents`, model)).data;

export const postCopyDocument = async (docId: string) =>
  (await axiosInstance.post<string>(`/api/v1/dynamic-forms/documents/${docId}/copy`)).data;

export const getDocuments = async (accountId: string) =>
  (await axiosInstance.get<DocumentDashboardModel[]>(`/api/v1/dynamic-forms/documents`, { params: { accountId } })).data;

export const getDocument = async (docId: string) =>
  (await axiosInstance.get<DocumentModel>(`/api/v1/dynamic-forms/documents/${docId}`)).data;

export const getDocumentCollections = async (docId: string) =>
  (await axiosInstance.get<DocumentCollectionModel[]>(`/api/v1/dynamic-forms/documents/${docId}/collections`)).data;

export const putDocumentCollections = async (docId: string, collectionIds: string[]) =>
  (await axiosInstance.put<void>(`/api/v1/dynamic-forms/documents/${docId}/collections`, collectionIds)).data;

export const getDocumentVersion = async (docId: string, versionId: string) =>
  (await axiosInstance.get<DocumentModel>(`/api/v1/dynamic-forms/documents/${docId}/versions/${versionId}`)).data;

export const getDocumentVersions = async (docId: string) =>
  (await axiosInstance.get<DocumentVersionModel[]>(`/api/v1/dynamic-forms/documents/${docId}/versions`)).data;

export const getDocumentComparison = async (docId: string, versionAId: string, versionBId: string) =>
  (
    await axiosInstance.get<DocumentDiff>(`/api/v1/dynamic-forms/documents/${docId}/compare`, {
      params: { versionA: versionAId, versionB: versionBId },
    })
  ).data;

export const deleteDocument = async (docId: string) =>
  (await axiosInstance.delete<void>(`/api/v1/dynamic-forms/documents/${docId}`)).data;

export const putDigitalVersion = async (docId: string, data: UpdateDocumentDigitalVersionModel) =>
  (await axiosInstance.put<void>(`/api/v1/dynamic-forms/documents/${docId}/digital-version`, data)).data;

export const patchUpdateDocument = async (docId: string, data: any[]) =>
  (
    await axiosInstance.patch<void>(`/api/v1/dynamic-forms/documents/${docId}`, data, {
      headers: { 'Content-Type': 'application/json-patch+json' },
    })
  ).data;

export const postStartDocumentVerification = async (docId: string) =>
  (await axiosInstance.post<void>(`/api/v1/dynamic-forms/documents/${docId}/start-verification`)).data;

export const postCancelDocumentVerification = async (docId: string) =>
  (await axiosInstance.post<void>(`/api/v1/dynamic-forms/documents/${docId}/cancel-verification`)).data;

export const postApproveDocumentVerification = async (docId: string) =>
  (await axiosInstance.post<void>(`/api/v1/dynamic-forms/documents/${docId}/approve-verification`)).data;

export const postDeclineDocumentVerification = async (docId: string, declineReason: string) =>
  (await axiosInstance.post<void>(`/api/v1/dynamic-forms/documents/${docId}/decline-verification`, { declineReason })).data;

export const postRetractDocumentVerification = async (docId: string) =>
  (await axiosInstance.post<void>(`/api/v1/dynamic-forms/documents/${docId}/retract-verification`)).data;

export const postSendDocumentForPublication = async (docId: string) =>
  (await axiosInstance.post<void>(`/api/v1/dynamic-forms/documents/${docId}/send-for-publication`)).data;

export const postSendDocumentForPublicationEditorial = async (docId: string, notes: string) =>
  (await axiosInstance.post<void>(`/api/v1/dynamic-forms/documents/${docId}/send-for-publication-editorial`, { notes })).data;

export const postLicenseeAdminSendDocumentToPublish = async (docId: string, licenseeAdminReviewComment: string) =>
  (await axiosInstance.post<void>(`/api/v1/dynamic-forms/documents/${docId}/licensee-admin-send-to-publish`, { licenseeAdminReviewComment })).data;

export const postCancelDocumentPublication = async (docId: string) =>
  (await axiosInstance.post<void>(`/api/v1/dynamic-forms/documents/${docId}/cancel-publication`)).data;

export const postDeclineDocumentPublication = async (docId: string, declineReason: string) =>
  (await axiosInstance.post<void>(`/api/v1/dynamic-forms/documents/${docId}/decline-publication`, { declineReason })).data;

export const postPublishDocument = async (docId: string) =>
  (await axiosInstance.post<void>(`/api/v1/dynamic-forms/documents/${docId}/publish`)).data;

export const postDepublishDocument = async (
  docId: string,
  depublicationReason?: string,
  depublishType?: string | null,
) =>
  (await axiosInstance.post<void>(`/api/v1/dynamic-forms/documents/${docId}/depublish`, { depublicationReason, depublishType })).data;

export const postDeregisterDocument = async (docId: string) =>
  (await axiosInstance.post<void>(`/api/v1/dynamic-forms/documents/${docId}/deregister`)).data;

export const postCancelDocumentDeregistration = async (docId: string) =>
  (await axiosInstance.post<void>(`/api/v1/dynamic-forms/documents/${docId}/cancel-deregistration`)).data;

export const postStartDocumentUpdate = async (docId: string) =>
  (await axiosInstance.post<void>(`/api/v1/dynamic-forms/documents/${docId}/start-update`)).data;

export const postStartDocumentEditorialUpdate = async (docId: string) =>
  (await axiosInstance.post<void>(`/api/v1/dynamic-forms/documents/${docId}/start-editorial-update`)).data;

export const postCancelDocumentUpdate = async (docId: string) =>
  (await axiosInstance.post<void>(`/api/v1/dynamic-forms/documents/${docId}/cancel-update`)).data;

export const postGenerateDocumentRegistrationCertificate = async (docId: string) =>
  (await axiosInstance.post<void>(`/api/v1/dynamic-forms/documents/${docId}/generate-registration-certificate`)).data;

// Returns the full response: the caller reads the server-provided file name from Content-Disposition.
export const getDocumentIlcdEpdBundle = async (docId: string) =>
  await axiosInstance.get<Blob>(`/api/v1/dynamic-forms/documents/${docId}/ilcd-epd-bundle`, { responseType: 'blob' });

export const getDocumentRegistrationCertificateData = async (versionId: string) =>
  (await axiosInstance.get(`/api/v1/dynamic-forms/documents/versions/${versionId}/certificate-data`)).data;
